import { AGAIN_DELAY_MINUTES, scheduleReview } from '@memorizecards/shared/srs';
import type { ReviewGrade } from '@memorizecards/shared/srs';
import { Badge, Card, Rule, Section, Table, Tabs, Td, Th, Tr } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TituloPagina } from '../components/estado';
import {
  ArvoreDecisao,
  DiagramaBotoes,
  EsbocoCronograma,
  EsbocoDesempenho,
  FluxoEstudo,
  LinhaTempoIntervalos,
} from './ajuda/Ilustracoes';

const BOTOES: Array<{
  grade: ReviewGrade;
  rotulo: string;
  tecla: string;
  resumo: string;
  detalhe: string;
  tom: 'danger' | 'warning' | 'accent' | 'neutral';
}> = [
  {
    grade: 1,
    rotulo: 'Errei',
    tecla: '1',
    resumo: 'Volta em 10 minutos',
    detalhe:
      'Use quando errou ou não sabia a resposta. O intervalo zera e o cartão reaparece na mesma sessão (ou logo depois). Conta como erro nas estatísticas.',
    tom: 'danger',
  },
  {
    grade: 2,
    rotulo: 'Difícil',
    tecla: '2',
    resumo: 'Intervalo × 1,2 (mínimo 1 dia)',
    detalhe:
      'Lembrou com esforço ou hesitação. O intervalo cresce devagar — ideal para conteúdo ainda frágil. Conta como acerto.',
    tom: 'warning',
  },
  {
    grade: 3,
    rotulo: 'Bom',
    tecla: '3',
    resumo: 'Intervalo × 2,5 (ou 1 dia se for novo)',
    detalhe:
      'Resposta correta com dificuldade normal. É o botão mais usado no dia a dia. Conta como acerto.',
    tom: 'accent',
  },
  {
    grade: 4,
    rotulo: 'Fácil',
    tecla: '4',
    resumo: 'Intervalo × 3,5 (mínimo 4 dias)',
    detalhe:
      'Resposta imediata e confiante. Empurra a revisão para mais longe — use só quando realmente domina o cartão. Conta como acerto.',
    tom: 'neutral',
  },
];

const INTERVALOS_EXEMPLO = [0, 1, 4, 10, 30] as const;

function projecao(intervalo: number, grade: ReviewGrade): string {
  if (grade === 1) return `${AGAIN_DELAY_MINUTES} min`;
  const dias = scheduleReview({ intervalo }, grade).intervalo;
  return `${dias} d`;
}

function TabVisaoGeral(): ReactNode {
  return (
    <div className="space-y-8">
      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Repetição espaçada</h2>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          O MemorizeCards agenda cada flashcard individualmente. Em vez de revisar tudo todo dia, o
          sistema calcula <strong className="text-mc-ink">quando</strong> cada cartão deve voltar com
          base na sua honestidade ao responder. Acertos consistentes aumentam o intervalo; erros trazem
          o cartão de volta quase na hora.
        </p>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          Esse método — repetição espaçada — é o mesmo princípio usado por estudantes de medicina,
          idiomas e concursos há décadas: revisar no momento certo, nem cedo demais (perda de tempo) nem
          tarde demais (esquecimento).
        </p>
      </Card>

      <FluxoEstudo />

      <Card padding="md">
        <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Conceitos importantes</h3>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            {
              termo: 'Intervalo',
              def: 'Quantos dias o cartão “descansa” até a próxima revisão. Começa em zero (cartão novo).',
            },
            {
              termo: 'Fila de revisão',
              def: 'Cartões cujo prazo já venceu aparecem quando você estuda. Erros voltam em 10 minutos.',
            },
            {
              termo: 'Sequência (streak)',
              def: 'Dias seguidos em que você revisou pelo menos um cartão. Pular um dia reinicia a contagem.',
            },
            {
              termo: 'Maturidade',
              def: 'Quão “dominado” está o cartão — intervalos maiores indicam conteúdo mais consolidado.',
            },
          ].map((item) => (
            <div key={item.termo}>
              <dt className="font-medium text-mc-accent">{item.termo}</dt>
              <dd className="mt-1 text-sm text-mc-ink2">{item.def}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <LinhaTempoIntervalos />
    </div>
  );
}

function TabBotoes(): ReactNode {
  return (
    <div className="space-y-8">
      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Os quatro botões</h2>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          Depois de virar o cartão e ver a resposta, escolha uma das quatro opções. Não existe botão
          “Médio” — o equivalente no MemorizeCards é <strong className="text-mc-ink">Bom</strong>,
          para uma resposta correta com esforço razoável.
        </p>
        <p className="mt-3 text-sm text-mc-ink3">
          Atalhos de teclado: teclas <kbd className="rounded border border-mc-line px-1">1</kbd> a{' '}
          <kbd className="rounded border border-mc-line px-1">4</kbd> durante o estudo.
        </p>
      </Card>

      <DiagramaBotoes />
      <ArvoreDecisao />

      <div className="grid gap-4 md:grid-cols-2">
        {BOTOES.map((b) => (
          <Card key={b.grade} padding="md" className="border-mc-line">
            <div className="flex items-start justify-between gap-3">
              <Badge tone={b.tom}>{b.rotulo}</Badge>
              <span className="mc-mono text-xs text-mc-ink3">Tecla {b.tecla}</span>
            </div>
            <p className="mt-3 font-medium text-mc-ink">{b.resumo}</p>
            <p className="mt-2 text-sm text-mc-ink2 leading-relaxed">{b.detalhe}</p>
          </Card>
        ))}
      </div>

      <Card padding="md">
        <h3 className="font-mc-display text-lg font-semibold text-mc-ink">
          Tabela de intervalos (mesma lógica do servidor)
        </h3>
        <p className="mt-2 text-sm text-mc-ink2">
          Os valores abaixo são calculados pelo algoritmo real da plataforma — o mesmo que grava sua
          próxima revisão e o rótulo exibido nos botões durante o estudo.
        </p>
        <div className="mt-4 overflow-x-auto">
          <Table>
            <thead>
              <Tr>
                <Th>Intervalo atual</Th>
                <Th>Errei</Th>
                <Th>Difícil</Th>
                <Th>Bom</Th>
                <Th>Fácil</Th>
              </Tr>
            </thead>
            <tbody>
              {INTERVALOS_EXEMPLO.map((intervalo) => (
                <Tr key={intervalo}>
                  <Td>
                    {intervalo === 0 ? 'Cartão novo (0 d)' : `${intervalo} dias`}
                  </Td>
                  <Td>{projecao(intervalo, 1)}</Td>
                  <Td>{projecao(intervalo, 2)}</Td>
                  <Td>{projecao(intervalo, 3)}</Td>
                  <Td>{projecao(intervalo, 4)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Rule className="my-4" />
        <ul className="list-disc space-y-2 pl-5 text-sm text-mc-ink2">
          <li>
            <strong>Errei:</strong> intervalo volta a 0; próxima revisão em {AGAIN_DELAY_MINUTES}{' '}
            minutos.
          </li>
          <li>
            <strong>Difícil:</strong> novo intervalo = máximo(1, parte inteira do atual × 1,2).
          </li>
          <li>
            <strong>Bom:</strong> se o intervalo era 0, vira 1 dia; senão × 2,5 (mínimo 1 dia).
          </li>
          <li>
            <strong>Fácil:</strong> × 3,5 com piso de 4 dias — mesmo em cartões novos.
          </li>
        </ul>
      </Card>
    </div>
  );
}

function TabEstudar(): ReactNode {
  return (
    <div className="space-y-8">
      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Como estudar</h2>
        <ol className="mt-4 list-decimal space-y-3 pl-5 text-mc-ink2">
          <li>
            Em <Link to="/baralhos" className="text-mc-accent underline">Flashcards</Link>, escolha um
            baralho e toque em <strong className="text-mc-ink">Estudar</strong>.
          </li>
          <li>
            Leia a pergunta na frente do cartão. Tente responder mentalmente antes de virar.
          </li>
          <li>
            Toque na carta ou pressione espaço para ver a resposta no verso.
          </li>
          <li>
            Escolha Errei, Difícil, Bom ou Fácil. O rótulo de cada botão mostra quando aquele cartão
            voltará se você clicar nele.
          </li>
          <li>
            O próximo cartão aparece automaticamente. Cartões marcados como Errei retornam após 10
            minutos na mesma sessão.
          </li>
        </ol>
      </Card>

      <Card padding="md">
        <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Modos e filtros</h3>
        <dl className="mt-4 space-y-4">
          {[
            {
              termo: 'Revisão do dia',
              def: 'Só cartões cujo prazo já venceu — o modo recomendado para manter a rotina.',
            },
            {
              termo: 'Estudar tudo',
              def: 'Inclui cartões novos e vencidos. Útil quando quer avançar no baralho.',
            },
            {
              termo: 'Por assunto',
              def: 'Filtra sub-tópicos dentro do baralho (quando o conteúdo tem hierarquia).',
            },
            {
              termo: 'Janela de dias',
              def: 'Antecipa revisões que venceriam nos próximos N dias — bom antes de provas.',
            },
          ].map((item) => (
            <div key={item.termo}>
              <dt className="font-medium text-mc-ink">{item.termo}</dt>
              <dd className="mt-1 text-sm text-mc-ink2">{item.def}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card padding="md">
        <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Recursos durante o estudo</h3>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-mc-ink2">
          <li>
            <strong>Favorita</strong> — marque cartões difíceis para revisar depois em Favoritas.
          </li>
          <li>
            <strong>Nota pessoal</strong> — anote mnemônicos ou referências; fica só para você.
          </li>
          <li>
            <strong>Áudio</strong> — ouça a pergunta ou resposta em voz alta (quando disponível).
          </li>
          <li>
            <strong>Reportar</strong> — avise a equipe sobre erro de conteúdo no cartão.
          </li>
          <li>
            <strong>Cronômetro de foco</strong> — acompanhe quanto tempo dedica à sessão.
          </li>
        </ul>
      </Card>
    </div>
  );
}

function TabPaineis(): ReactNode {
  return (
    <div className="space-y-8">
      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Desempenho</h2>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          A página <Link to="/desempenho" className="text-mc-accent underline">Desempenho</Link> é o
          painel diário: sequência de dias, meta de cartões, taxa de acerto recente e o que está por
          vencer. Use para manter o hábito e enxergar tendências dos últimos meses.
        </p>
      </Card>

      <EsbocoDesempenho />

      <Card padding="md">
        <h3 className="font-mc-display text-lg font-semibold text-mc-ink">O que cada bloco significa</h3>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            {
              termo: 'Sequência',
              def: 'Dias consecutivos com pelo menos uma revisão. Estudar duas vezes no mesmo dia não aumenta o número.',
            },
            {
              termo: 'Meta de hoje',
              def: 'Cartões revisados hoje versus sua meta diária configurável.',
            },
            {
              termo: 'Taxa de acerto',
              def: 'Proporção de respostas que não foram Errei no período recente.',
            },
            {
              termo: 'Heatmap',
              def: 'Calendário visual: quanto mais escuro, mais cartões você revisou naquele dia.',
            },
            {
              termo: 'Por matéria',
              def: 'Radar e barras mostram onde você está forte ou fraco entre os baralhos.',
            },
            {
              termo: 'Vencendo em breve',
              def: 'Cartões que voltarão nos próximos dias — ajuda a planejar a carga de estudo.',
            },
          ].map((item) => (
            <div key={item.termo}>
              <dt className="font-medium text-mc-accent">{item.termo}</dt>
              <dd className="mt-1 text-sm text-mc-ink2">{item.def}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Rule />

      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Estatísticas</h2>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          Em <Link to="/estatisticas" className="text-mc-accent underline">Estatísticas</Link> você
          aprofunda: filtre por período (7, 30, 90 dias ou 1 ano) e por baralho. Veja distribuição de
          acertos/erros, maturidade das revisões, tempo de estudo e histórico de simulados.
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-mc-ink2">
          <li>
            <strong>Maturidade</strong> — quantos cartões estão em cada faixa de intervalo (novos,
            curtos, médios, longos).
          </li>
          <li>
            <strong>Histórico de respostas</strong> — quantas vezes você marcou cada botão no período.
          </li>
          <li>
            <strong>Simulados</strong> — notas e evolução dos testes cronometrados.
          </li>
          <li>
            <strong>Zerar progresso</strong> — reinicia intervalos de um baralho (ação irreversível;
            use com cuidado).
          </li>
        </ul>
      </Card>
    </div>
  );
}

function TabFerramentas(): ReactNode {
  return (
    <div className="space-y-8">
      <Card padding="md">
        <h2 className="font-mc-display text-xl font-semibold text-mc-ink">Cronograma</h2>
        <p className="mt-3 text-mc-ink2 leading-relaxed">
          O <Link to="/cronograma" className="text-mc-accent underline">Cronograma</Link> mostra quantos
          cartões estão agendados para cada dia da semana ou mês. É a visão “de cima” do algoritmo: se
          você marcar muitos cartões como Fácil hoje, verá picos mais distantes; muitos Errei concentram
          revisões nos próximos dias.
        </p>
      </Card>

      <EsbocoCronograma />

      <div className="grid gap-4 md:grid-cols-2">
        <Card padding="md">
          <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Simulado</h3>
          <p className="mt-3 text-sm text-mc-ink2 leading-relaxed">
            Teste cronometrado sem ver intervalos — apenas acertou ou errou. A nota vai de 0 a 10 e
            entra no histórico de Estatísticas. Ideal para medir preparo real antes da prova.
          </p>
          <Link to="/simulado" className="mt-4 inline-block text-sm text-mc-accent underline">
            Ir para Simulado →
          </Link>
        </Card>

        <Card padding="md">
          <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Favoritas</h3>
          <p className="mt-3 text-sm text-mc-ink2 leading-relaxed">
            Cartões marcados com estrela durante o estudo ficam reunidos aqui. Estude só os difíceis
            quando quiser uma sessão extra, sem alterar o agendamento normal dos outros.
          </p>
          <Link to="/favoritas" className="mt-4 inline-block text-sm text-mc-accent underline">
            Ir para Favoritas →
          </Link>
        </Card>

        <Card padding="md">
          <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Caderno</h3>
          <p className="mt-3 text-sm text-mc-ink2 leading-relaxed">
            Anotações livres por baralho — resumos, mapas mentais, links. Separado das notas por cartão
            e não interfere nos intervalos de revisão.
          </p>
          <Link to="/caderno" className="mt-4 inline-block text-sm text-mc-accent underline">
            Ir para Caderno →
          </Link>
        </Card>

        <Card padding="md">
          <h3 className="font-mc-display text-lg font-semibold text-mc-ink">Dicas finais</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-mc-ink2">
            <li>Prefira sessões curtas e diárias a maratonas esporádicas.</li>
            <li>Seja honesto nos botões — o algoritmo só funciona com feedback real.</li>
            <li>Use Difícil para conteúdo frágil; Fácil só quando dominar de verdade.</li>
            <li>Confira o Cronograma antes de provas para distribuir a carga.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

export function Ajuda(): ReactNode {
  return (
    <Section spacing="md">
      <TituloPagina
        eyebrow="Suporte"
        titulo="Central de ajuda"
        descricao="Guia completo sobre repetição espaçada, botões de resposta, estatísticas e ferramentas da plataforma."
      />

      <Tabs
        items={[
          { id: 'visao', label: 'Como funciona', content: <TabVisaoGeral /> },
          { id: 'botoes', label: 'Botões', content: <TabBotoes /> },
          { id: 'estudar', label: 'Estudar', content: <TabEstudar /> },
          { id: 'paineis', label: 'Desempenho', content: <TabPaineis /> },
          { id: 'ferramentas', label: 'Ferramentas', content: <TabFerramentas /> },
        ]}
      />

      <Card padding="md" className="mt-8 border-mc-accent/20 bg-mc-accent/5">
        <p className="text-sm text-mc-ink2">
          Ainda com dúvidas? Escreva para{' '}
          <a href="mailto:contato@memorizecards.com.br" className="text-mc-accent underline">
            contato@memorizecards.com.br
          </a>{' '}
          — respondemos em até um dia útil.
        </p>
      </Card>
    </Section>
  );
}
